import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { hideSplash, configureStatusBar, registerNativePush, isNativePlatform } from "./lib/capacitor";
import OnboardingAssessment from "./components/OnboardingAssessment";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./lib/theme";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import MentalTraining from "./pages/MentalTraining";
import PerformanceTracker from "./pages/PerformanceTracker";
import Goals from "./pages/Goals";
import AICoach from "./pages/AICoach";
import PhysicalWorkouts from "./pages/PhysicalWorkouts";
import TrainingPlans from "./pages/TrainingPlans";
import Teams from "./pages/Teams";
import SkillAssessment from "./pages/SkillAssessment";
import MistakeManagement from "./pages/MistakeManagement";
import NutritionGuide from "./pages/NutritionGuide";
import VideoLibrary from "./pages/VideoLibrary";
import MindfulnessGames from "./pages/MindfulnessGames";
import Routines from "./pages/Routines";
import Messages from "./pages/Messages";
import Schedule from "./pages/Schedule";
import News from "./pages/News";
import TeamReports from "./pages/TeamReports";
import PerformanceJournal from "./pages/PerformanceJournal";
import DailyLog from "./pages/DailyLog";
import PerformanceTrends from "./pages/PerformanceTrends";
import CalendarManagement from "./pages/CalendarManagement";
import NotificationSettings from "./pages/NotificationSettings";
import WellnessTrends from "./pages/WellnessTrends";
import AthleteOverview from "./pages/AthleteOverview";
import Favorites from "./pages/Favorites";
import UserManagement from "./pages/UserManagementEnhanced";
import UserSettings from "./pages/UserSettings";
import SharedPlanView from "./pages/SharedPlanView";
import PrePerformanceRoutines from "./pages/PrePerformanceRoutines";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import ChatMessages from "./pages/ChatMessages";
import DailyMetricsModal from "./components/DailyMetricsModal";
import AppFooter from "./components/AppFooter";
import UnreadMessagePopup from "./components/UnreadMessagePopup";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import MobileNavigation from "./components/MobileNavigation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDailyMetrics, setShowDailyMetrics] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [metricsChecked, setMetricsChecked] = useState(() => {
    return sessionStorage.getItem('metricsChecked') === 'true';
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);

    // Capacitor native init
    hideSplash();
    configureStatusBar();
    if (isNativePlatform) {
      registerNativePush();
    }
  }, []);

  // Check if athlete needs to complete onboarding assessment
  useEffect(() => {
    const checkOnboarding = async () => {
      if (user && user.role === 'athlete') {
        try {
          const token = localStorage.getItem('token');
          const res = await axios.get(`${API}/settings/onboarding-status`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (!res.data.onboarding_complete) {
            setShowOnboarding(true);
          }
        } catch (e) {
          console.error("Failed to check onboarding:", e);
        }
      }
      setOnboardingChecked(true);
    };
    checkOnboarding();
  }, [user]);

  // Check if athlete needs to submit daily metrics — only after onboarding is resolved
  useEffect(() => {
    const checkDailyMetrics = async () => {
      if (!onboardingChecked || showOnboarding) return;
      if (user && user.role === 'athlete' && !metricsChecked) {
        try {
          const token = localStorage.getItem('token');
          const response = await axios.get(`${API}/daily-metrics/today`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (response.data.required && !response.data.submitted) {
            // Delay showing the modal so page content loads first
            // Don't show if onboarding is active
            setTimeout(() => {
              if (!document.querySelector('[data-onboarding]')) {
                setShowDailyMetrics(true);
              }
            }, 1500);
          }
          setMetricsChecked(true);
        } catch (error) {
          console.error("Failed to check daily metrics:", error);
          setMetricsChecked(true);
        }
      }
    };

    checkDailyMetrics();
  }, [user, metricsChecked, onboardingChecked, showOnboarding]);

  const handleMetricsComplete = () => {
    setShowDailyMetrics(false);
    setMetricsChecked(true);
    sessionStorage.setItem('metricsChecked', 'true');
    window.dispatchEvent(new CustomEvent('checkin-complete'));
  };

  const handleOnboardingComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/settings/onboarding-complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error("Failed to mark onboarding complete:", e);
    }
    setShowOnboarding(false);
  };

  const login = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <ThemeProvider>
    <AuthProvider>
      <div className="App min-h-screen bg-background text-foreground">
        <Toaster position="top-right" />
        <BrowserRouter>
          {/* Mobile Navigation for authenticated users */}
          {user && (
            <div className="md:hidden">
              <MobileNavigation onLogout={logout} user={user} />
            </div>
          )}
          <div className={user ? "pt-14 pb-16 md:pt-0 md:pb-0" : ""}>
            <Routes>
            <Route path="/" element={!user ? <LandingPage onLogin={login} /> : <Navigate to="/dashboard" />} />
            <Route path="/dashboard" element={user ? <Dashboard user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/training" element={user ? <MentalTraining user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/performance" element={user ? <PerformanceTracker user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/goals" element={user ? <Goals user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/ai-coach" element={user ? <AICoach user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/workouts" element={user ? <PhysicalWorkouts user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/plans" element={user ? <TrainingPlans user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/teams" element={user ? <Teams user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/assessment" element={user ? <SkillAssessment user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/mistakes" element={user ? <MistakeManagement user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/nutrition" element={user ? <NutritionGuide user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/videos" element={user ? <VideoLibrary user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/mindfulness-games" element={user ? <MindfulnessGames user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/routines" element={user ? <Routines user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/messages" element={user ? <ChatMessages user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/schedule" element={user ? <Schedule user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/news" element={user ? <News user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/journal" element={user ? <PerformanceJournal user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/daily-log" element={user ? <DailyLog user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/trends" element={user ? <PerformanceTrends user={user} onLogout={logout} /> : <Navigate to="/" />} />

            <Route path="/team-reports" element={user ? <TeamReports user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/notifications" element={user ? <NotificationSettings user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/wellness-trends" element={user ? <WellnessTrends user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/athlete-overview" element={user ? <AthleteOverview user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/favorites" element={user ? <Favorites user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/user-management" element={user ? <UserManagement user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/settings" element={user ? <UserSettings user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/shared-plan/:shareId" element={<SharedPlanView />} />
            <Route path="/pre-routines" element={user ? <PrePerformanceRoutines user={user} onLogout={logout} /> : <Navigate to="/" />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            </Routes>
            {/* Footer on all pages */}
            <AppFooter />
          </div>
        {/* Unread Message Popup — shows when coach/admin sent messages */}
        {user && !showOnboarding && !showDailyMetrics && (
          <UnreadMessagePopup />
        )}

        </BrowserRouter>
        
        {/* Daily Metrics Modal for Athletes - only after onboarding is done */}
        {user && user.role === 'athlete' && !showOnboarding && (
          <DailyMetricsModal 
            open={showDailyMetrics} 
            onComplete={handleMetricsComplete}
          />
        )}

        {/* Onboarding Assessment - Required for new athletes */}
        {user && user.role === 'athlete' && showOnboarding && (
          <OnboardingAssessment
            open={showOnboarding}
            onComplete={handleOnboardingComplete}
          />
        )}
      </div>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;